import React, { Component } from 'react';
import { get, isEmpty, maxBy } from 'lodash';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { NavLink } from 'react-router-dom';
import { DatePicker, Icon, TimePicker, Button } from 'antd';

import { SeismicAction } from '~/actions';
import moment from '~/_moment';
import { ContentSection } from '~/components';
import { DivMargin15, H1Title, H2Title } from '~/containers/styles';
import ContentData from '~/hoc/ContentData';

import MaxDataInfo from './MaxDataInfo';
import { AreaGraphConfig, seriesConfig, LineChartConfig } from './config';
import {
  FilterContainer,
  H3TitleExtraMargin,
  EmptyStatePanel,
  FilteAndOptionContainer,
} from './styles';

const ReactHichart = require('react-highcharts');
require('highcharts-more')(ReactHichart.Highcharts);

const ParseDateTimeFormat = 'YYYY-MM-DD[T]HH:mm:ss';
const DateTimeFormat = 'YYYY-MM-DD HH:mm:ss';

const dateFormat = 'YYYY-MM-DD';
const timeFormat = 'HH:mm:ss';

class SeismicWave extends Component {
  static propTypes = {
    shouldPin: PropTypes.bool,
  };

  static defaultProps = {
    shouldPin: false,
  };

  state = {
    isLoading: true,
    station: '',
    dateTime: '1970-01-01 00:00:00',
    date: '1970-01-01',
    time: '00:00:00',
    isRealtime: false,
    isShowMax: false,
  };

  UNSAFE_componentWillMount() {
    this.setInitial(this.props);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const {
      seismic,
      match: {
        params: { station, time },
      },
    } = nextProps;

    // If no specify time from URL, it's mean try to get data from current
    // if (!time) {
    //   this.enableRealtime();
    // }

    if (station !== this.props.match.params.station || time !== this.props.match.params.time) {
      this.setInitial(nextProps);
      return;
    }

    const { dateTime, isRealtime } = this.state;
    if (
      seismic.stream.requestTime.format(DateTimeFormat) === dateTime ||
      (seismic.isLoading === false && isEmpty(seismic.stream.data)) ||
      isRealtime
    ) {
      this.setState({ isLoading: false });
    }
  }

  componentWillUnmount() {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  onDateChange = (currentDate) => {
    const { time } = this.state;
    const date = currentDate.format(dateFormat);
    this.setState({
      dateTime: `${date} ${time}`,
      date,
    });
  };

  onTimeChange = (currentTime) => {
    const { date } = this.state;
    const time = currentTime.format(timeFormat);
    this.setState({
      dateTime: `${date} ${time}`,
      time,
    });
  };

  setInitial = (props) => {
    const {
      match: {
        params: { station, time },
      },
    } = props;
    const dateTime = time ? moment(time, ParseDateTimeFormat) : moment();

    this.setState(
      {
        station,
        dateTime: dateTime.format(DateTimeFormat),
        date: dateTime.format(dateFormat),
        time: dateTime.format(timeFormat),
        isRealtime: !time,
      },
      () => {
        this.getData();
      },
    );
  };

  getData = (getCurrent = false) => {
    const { dateTime, station } = this.state;
    this.setState({ isLoading: true }, () => {
      if (getCurrent) {
        this.props.getSeismicWave({ dateTime: moment().format(DateTimeFormat), station });
      } else {
        this.props.getSeismicWave({ dateTime, station });
      }
    });
  };

  // enableRealtime = () => {
  //   const { isRealtime } = this.state;
  //   if (!isRealtime) return;
  //   if (this.interval) return;

  //   this.interval = setTimeout(() => {
  //     const {
  //       match: {
  //         params: { time },
  //       },
  //     } = this.props;
  //     if (!time) {
  //       this.getData(true);
  //       this.enableRealtime();
  //     }
  //   }, 10000);
  // };

  prepareGraphData = (data) => {
    const ch0 = [];
    const ch1 = [];
    const ch2 = [];
    const maxCh0 = [];
    const maxCh1 = [];
    const maxCh2 = [];

    Object.keys(data).forEach((unixIndex) => {
      const currentData = data[unixIndex];
      const time = moment(Number(unixIndex) * 1000).format('HH:mm:ss');

      ch0.push([time, currentData.ch0[0], currentData.ch0[1]]);
      ch1.push([time, currentData.ch1[0], currentData.ch1[1]]);
      ch2.push([time, currentData.ch2[0], currentData.ch2[1]]);

      // Old logic
      // maxCh0.push([time, currentData.ch0_max]);
      // maxCh1.push([time, currentData.ch1_max]);
      // maxCh2.push([time, currentData.ch2_max]);

      // New logic to show max val with operator
      maxCh0.push([time, maxBy(currentData.ch0, (v) => Math.abs(v))]);
      maxCh1.push([time, maxBy(currentData.ch1, (v) => Math.abs(v))]);
      maxCh2.push([time, maxBy(currentData.ch2, (v) => Math.abs(v))]);
    });

    return { ch0, ch1, ch2, maxCh0, maxCh1, maxCh2 };
  };

  forceDisable = (current) => {
    const {
      profile: { firstRun },
    } = this.props;
    const disableFuture = current >= moment().endOf('day');
    if (firstRun) {
      return current <= moment(firstRun, 'YYYY-MM-DD').endOf('day') || disableFuture;
    }

    return disableFuture;
  };

  renderShowMaxPin = (absoluteData) => {
    const { isShowMax } = this.state;
    if (!isShowMax) return { pinConfig: {} };

    const maxData = maxBy(absoluteData, (data) => data[1]);
    const timeIndex = absoluteData.findIndex((item) => item[0] === maxData[0]);

    const pinConfig = {
      xAxis: {
        type: 'category',
        plotLines: [
          {
            color: '#ffcccc',
            width: 1,
            value: timeIndex,
            zIndex: 0,
          },
        ],
      },
    };

    return { pinConfig, maxData, timeIndex };
  };

  renderGraph = ({ graphData, shouldPin }) => {
    const { dateTime, station, isShowMax } = this.state;
    const { profile } = this.props;

    const time = moment(dateTime).format('HH:mm:ss');
    const pinLabel = shouldPin && {
      xAxis: {
        type: 'category',
        plotLines: [
          {
            color: '#ffcccc',
            width: 1,
            value: graphData.ch0.findIndex((item) => item[0] === time),
            zIndex: 0,
          },
        ],
      },
    };

    const pinMaxDataCh0 = this.renderShowMaxPin(graphData.maxCh0);
    const pinMaxDataCh1 = this.renderShowMaxPin(graphData.maxCh1);
    const pinMaxDataCh2 = this.renderShowMaxPin(graphData.maxCh2);

    return (
      <div>
        <DivMargin15>
          <div className="rows">
            <div className="col-xs-12">
              <ReactHichart
                config={{
                  ...LineChartConfig,
                  title: {
                    text: get(
                      profile,
                      `seismic.unit.${station}.summaryGraphTitle`,
                      'Realtime Acceleration',
                    ),
                  },
                  tooltip: {
                    valueSuffix: get(profile, `seismic.unit.${station}.unit`, 'm/s^2'),
                  },
                  series: [
                    { name: 'Ch0', data: graphData.maxCh0 },
                    { name: 'Ch1', data: graphData.maxCh1 },
                    { name: 'Ch2', data: graphData.maxCh2 },
                  ],
                  ...pinLabel,
                }}
              />
            </div>
          </div>
        </DivMargin15>

        <DivMargin15>
          <div className="rows">
            <div className="col-xs-12">
              <ReactHichart
                config={{
                  ...AreaGraphConfig,
                  title: { text: 'DAS Ch0' },
                  series: [{ ...seriesConfig, color: '#7cb5ec', data: graphData.ch0 }],
                  ...pinLabel,
                  ...pinMaxDataCh0.pinConfig,
                  tooltip: {
                    valueSuffix: get(profile, `seismic.unit.${station}.unit`, 'm/s^2'),
                  },
                }}
              />
            </div>

            {isShowMax && (
              <MaxDataInfo
                title="DAS Ch0"
                unit={get(profile, `seismic.unit.${station}.unit`, 'm/s^2')}
                data={graphData.ch0[pinMaxDataCh0.timeIndex]}
              />
            )}
          </div>
        </DivMargin15>

        <DivMargin15>
          <div className="rows">
            <div className="col-xs-12">
              <ReactHichart
                config={{
                  ...AreaGraphConfig,
                  title: { text: 'DAS Ch1' },
                  series: [{ ...seriesConfig, color: '#434348', data: graphData.ch1 }],
                  ...pinLabel,
                  ...pinMaxDataCh1.pinConfig,
                  tooltip: {
                    valueSuffix: get(profile, `seismic.unit.${station}.unit`, 'm/s^2'),
                  },
                }}
              />
            </div>

            {isShowMax && (
              <MaxDataInfo
                title="DAS Ch1"
                unit={get(profile, `seismic.unit.${station}.unit`, 'm/s^2')}
                data={graphData.ch1[pinMaxDataCh1.timeIndex]}
              />
            )}
          </div>
        </DivMargin15>

        <DivMargin15>
          <div className="rows">
            <div className="col-xs-12">
              <ReactHichart
                config={{
                  ...AreaGraphConfig,
                  title: { text: 'DAS Ch2' },
                  series: [{ ...seriesConfig, color: '#90ed7d', data: graphData.ch2 }],
                  ...pinLabel,
                  ...pinMaxDataCh2.pinConfig,
                  tooltip: {
                    valueSuffix: get(profile, `seismic.unit.${station}.unit`, 'm/s^2'),
                  },
                }}
              />
            </div>

            {isShowMax && (
              <MaxDataInfo
                title="DAS Ch2"
                unit={get(profile, `seismic.unit.${station}.unit`, 'm/s^2')}
                data={graphData.ch2[pinMaxDataCh2.timeIndex]}
              />
            )}
          </div>
        </DivMargin15>
      </div>
    );
  };

  render() {
    const {
      shouldPin,
      profile: {
        site_name: siteName,
        seismic: { station },
      },
      seismic: {
        stream: { startTime, endTime, data },
      },
    } = this.props;

    const { dateTime, station: stationId, isLoading, isShowMax } = this.state;

    const graphData = !!data && this.prepareGraphData(data);

    return (
      <ContentSection>
        <Helmet>
          <title>{`กราฟแรงสั่นสะเทือน สถานี${station[stationId]}`}</title>
        </Helmet>

        <ContentData
          isLoading={isLoading}
          title={`กำลังดึงข้อมูล ${moment(dateTime, DateTimeFormat).format('LL HH:mm')}`}
        >
          <DivMargin15>
            <div className="col-sm-6">
              <H1Title>{`โครงการ${siteName}`}</H1Title>
            </div>
            <div className="col-sm-6">
              <H2Title className="text-right">{station[stationId]}</H2Title>
              <FilterContainer>
                <FilteAndOptionContainer>
                  <DatePicker
                    defaultValue={moment(dateTime, DateTimeFormat)}
                    locale="th"
                    format="LL"
                    allowClear={false}
                    style={{ width: 170 }}
                    onChange={this.onDateChange}
                    disabledDate={this.forceDisable}
                  />
                  &nbsp;
                  <TimePicker
                    defaultValue={moment(dateTime, DateTimeFormat)}
                    format="HH:mm"
                    onChange={this.onTimeChange}
                    style={{ width: 100 }}
                  />
                  &nbsp;
                  <NavLink
                    to={`/seismic/station/${stationId}/${moment(dateTime).format(
                      ParseDateTimeFormat,
                    )}`}
                    className="ant-btn ant-btn-danger"
                    onClick={() => this.getData()}
                    disabled={isLoading}
                  >
                    <Icon type="reload" spin={isLoading} />
                    &nbsp; เรียกข้อมูล
                  </NavLink>
                </FilteAndOptionContainer>

                <FilteAndOptionContainer>
                  <Button
                    type={isShowMax ? 'danger' : 'default'}
                    onClick={() => this.setState({ isShowMax: !isShowMax })}
                  >
                    <Icon type="monitor" />
                    {` ${isShowMax ? 'ปิด' : 'เปิด'} การแสดงผลค่าสูงสุด`}
                  </Button>
                </FilteAndOptionContainer>
              </FilterContainer>
            </div>
          </DivMargin15>

          {!isEmpty(data) && (
            <DivMargin15>
              <div className="col-xs-12">
                <H3TitleExtraMargin className="text-center">{`ข้อมูลวันที่ ${startTime.format(
                  'LL',
                )} ช่วงเวลา ${startTime.format('HH:mm:ss')} ~ ${endTime.format(
                  'HH:mm:ss',
                )}`}</H3TitleExtraMargin>
              </div>
            </DivMargin15>
          )}

          {!isEmpty(data) ? (
            this.renderGraph({ graphData, shouldPin })
          ) : (
            <EmptyStatePanel>
              <H2Title className="text-center">ไม่สามารถแสดงข้อมูลในช่วงเวลานี้ได้</H2Title>
            </EmptyStatePanel>
          )}
        </ContentData>
      </ContentSection>
    );
  }
}

export default connect(
  (state) => ({
    profile: state.DamProfile,
    seismic: state.Seismic,
  }),
  {
    getSeismicWave: SeismicAction.getSeismicWave,
  },
)(SeismicWave);



// WEBPACK FOOTER //
// ./resources/assets/js/containers/SeismicWave/SeismicWave.js